import { useEffect, useMemo, useState } from "react";
import { Receipt, Plus, Trash2, Pencil, CheckCircle2, XCircle, ArrowUpRight, ArrowDownLeft, TriangleAlert } from "lucide-react";
import { api, money, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

const DIR_LABEL = { in: "Alacak", out: "Borç" };
const STATUS_LABEL = { pending: "Bekliyor", paid: "Ödendi", bounced: "Karşılıksız" };

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d - today) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function urgencyTone(d) {
  if (d === null) return "";
  if (d < 0) return "overdue";
  if (d <= 3) return "urgent";
  if (d <= 7) return "soon";
  return "";
}

const emptyForm = {
  direction: "out", party: "", amount: "", issue_date: todayISO(),
  due_date: "", bank: "", cheque_no: "", status: "pending",
  paid_date: "", note: "",
};

export default function Cheques() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("pending");
  const { form, set, setForm, reset } = useForm(emptyForm);

  const load = () => api.get("/cheques").then(setItems).catch(() => setError("Yüklenemedi."));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    let pIn = 0, pOut = 0, overdue = 0, upcoming7 = 0;
    for (const c of items) {
      if (c.status !== "pending") continue;
      const d = daysUntil(c.due_date);
      if (c.direction === "in") pIn += Number(c.amount) || 0;
      else pOut += Number(c.amount) || 0;
      if (d !== null && d < 0) overdue += 1;
      else if (d !== null && d <= 7) upcoming7 += 1;
    }
    return { pIn, pOut, overdue, upcoming7 };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "upcoming") return items.filter((c) => c.status === "pending" && (() => { const d = daysUntil(c.due_date); return d !== null && d >= 0 && d <= 7; })());
    if (filter === "overdue") return items.filter((c) => c.status === "pending" && (daysUntil(c.due_date) ?? 999) < 0);
    return items.filter((c) => c.status === filter);
  }, [items, filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = {
          status: form.status,
          paid_date: form.status === "paid" ? (form.paid_date || todayISO()) : null,
          note: form.note,
        };
        const updated = await api.patch(`/cheques/${editing.id}`, patch);
        setItems((x) => x.map((i) => (i.id === editing.id ? updated : i)));
      } else {
        const payload = {
          direction: form.direction,
          party: form.party,
          amount: Number(form.amount) || 0,
          issue_date: form.issue_date || null,
          due_date: form.due_date,
          bank: form.bank,
          cheque_no: form.cheque_no,
          status: "pending",
          note: form.note,
        };
        const rec = await api.post("/cheques", payload);
        setItems((x) => [...x, rec].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")));
      }
      reset();
      setEditing(null);
      setOpen(false);
    } catch { setError("Kaydedilemedi."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu çek silinsin mi?")) return;
    try { await api.del(`/cheques/${id}`); setItems((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c, paid_date: c.paid_date || todayISO() });
    setOpen(true);
  };

  const openNew = () => { reset(); setEditing(null); setOpen(true); };

  const markPaid = async (c) => {
    try {
      const updated = await api.patch(`/cheques/${c.id}`, { status: "paid", paid_date: todayISO() });
      setItems((x) => x.map((i) => (i.id === c.id ? updated : i)));
    } catch { setError("Güncellenemedi."); }
  };
  const markBounced = async (c) => {
    if (!window.confirm(`${c.party} çeki karşılıksız olarak işaretlensin mi?`)) return;
    try {
      const updated = await api.patch(`/cheques/${c.id}`, { status: "bounced" });
      setItems((x) => x.map((i) => (i.id === c.id ? updated : i)));
    } catch { setError("Güncellenemedi."); }
  };

  return (
    <>
      <PageHeader
        eyebrow="ÇEK / SENET TAKİBİ"
        title="Çekler"
        subtitle="Verdiğiniz ve aldığınız çeklerin vade takibi. Vadesi yaklaşan çekler uyarıya düşer."
        heroImage={HERO.cheques}
        heroTint={HERO_TINT.cheques}
        actions={
          <button className="primary-btn" data-testid="cheque-add-btn" onClick={openNew}>
            <Plus size={17} /> Çek ekle
          </button>
        }
      />

      {error && <div className="api-error" data-testid="cheque-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Bekleyen alacak" value={money(stats.pIn)} tone="green" testId="cheque-stat-in" />
        <StatMini label="Bekleyen borç" value={money(stats.pOut)} tone="red" testId="cheque-stat-out" />
        <StatMini label="Vadesi geçmiş" value={`${stats.overdue}`} tone="red" testId="cheque-stat-overdue" />
        <StatMini label="7 gün içinde" value={`${stats.upcoming7}`} tone="orange" testId="cheque-stat-upcoming" />
      </section>

      <div className="filter-row">
        <button className={`chip ${filter === "pending" ? "active" : ""}`} data-testid="cheque-filter-pending" onClick={() => setFilter("pending")}>Bekleyen</button>
        <button className={`chip ${filter === "upcoming" ? "active" : ""}`} data-testid="cheque-filter-upcoming" onClick={() => setFilter("upcoming")}>Yaklaşan (7 gün)</button>
        <button className={`chip ${filter === "overdue" ? "active" : ""}`} data-testid="cheque-filter-overdue" onClick={() => setFilter("overdue")}>Gecikmiş</button>
        <button className={`chip ${filter === "paid" ? "active" : ""}`} data-testid="cheque-filter-paid" onClick={() => setFilter("paid")}>Ödenmiş</button>
        <button className={`chip ${filter === "bounced" ? "active" : ""}`} data-testid="cheque-filter-bounced" onClick={() => setFilter("bounced")}>Karşılıksız</button>
        <button className={`chip ${filter === "all" ? "active" : ""}`} data-testid="cheque-filter-all" onClick={() => setFilter("all")}>Tümü</button>
      </div>

      <div className="panel">
        {filtered.length === 0 ? (
          <EmptyState icon={Receipt} title={items.length === 0 ? "Henüz çek yok" : "Bu filtrede çek yok"} text="Aldığınız ve verdiğiniz her çeki buradan takip edin. Vadesi 7 gün kaldığında otomatik uyarı düşer." actionLabel={items.length === 0 ? "İlk çeki ekle" : "Çek ekle"} onAction={openNew} testId="cheque-empty-add" />
        ) : (
          <div className="cheque-list">
            {filtered.map((c) => {
              const d = daysUntil(c.due_date);
              const tone = c.status === "pending" ? urgencyTone(d) : "";
              return (
                <div className={`cheque-card ${tone}`} key={c.id} data-testid={`cheque-item-${c.id}`}>
                  <div className={`cheque-dir ${c.direction}`}>
                    {c.direction === "in" ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                    <span>{DIR_LABEL[c.direction]}</span>
                  </div>
                  <div className="cheque-body">
                    <b>{c.party}</b>
                    <small>
                      {c.bank && `${c.bank} · `}{c.cheque_no && `No: ${c.cheque_no} · `}
                      Vade {trDate(c.due_date)}
                      {c.status === "pending" && d !== null && (
                        <span className={`cheque-days ${tone}`}>
                          {d < 0 ? ` · ${Math.abs(d)} gün gecikti` : d === 0 ? " · Bugün" : ` · ${d} gün kaldı`}
                        </span>
                      )}
                    </small>
                    {c.note && <em>{c.note}</em>}
                  </div>
                  <div className="cheque-right">
                    <strong className={c.direction === "in" ? "positive" : "negative"}>
                      {c.direction === "in" ? "+" : "−"}{money(c.amount)}
                    </strong>
                    <span className={`cheque-status ${c.status}`}>{STATUS_LABEL[c.status]}</span>
                  </div>
                  <div className="cheque-actions">
                    {c.status === "pending" && (
                      <>
                        <button className="mini-btn green" data-testid={`cheque-paid-${c.id}`} onClick={() => markPaid(c)} title="Ödendi olarak işaretle">
                          <CheckCircle2 size={14} />
                        </button>
                        <button className="mini-btn red" data-testid={`cheque-bounce-${c.id}`} onClick={() => markBounced(c)} title="Karşılıksız">
                          <XCircle size={14} />
                        </button>
                      </>
                    )}
                    <button className="mini-btn" data-testid={`cheque-edit-${c.id}`} onClick={() => openEdit(c)}>
                      <Pencil size={13} />
                    </button>
                    <button className="mini-btn danger" data-testid={`cheque-delete-${c.id}`} onClick={() => remove(c.id)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <Modal
          title={editing ? `Çeki güncelle · ${editing.party}` : "Yeni çek"}
          subtitle={editing ? "GÜNCELLE" : "ÇEK"}
          onClose={() => { setOpen(false); setEditing(null); }}
        >
          <form onSubmit={submit}>
            {!editing && (
              <>
                <FormField label="Tür">
                  <select required data-testid="cheque-form-direction" value={form.direction} onChange={(e) => set("direction", e.target.value)}>
                    <option value="out">Verdiğim çek (Borç)</option>
                    <option value="in">Aldığım çek (Alacak)</option>
                  </select>
                </FormField>
                <FormField label={form.direction === "in" ? "Kimden alındı" : "Kime verildi"}>
                  <input required data-testid="cheque-form-party" placeholder="Kişi / firma" value={form.party} onChange={(e) => set("party", e.target.value)} />
                </FormField>
                <div className="form-row">
                  <FormField label="Tutar (₺)">
                    <input required type="number" step="1" data-testid="cheque-form-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
                  </FormField>
                  <FormField label="Vade tarihi">
                    <input required type="date" data-testid="cheque-form-due" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
                  </FormField>
                </div>
                <div className="form-row">
                  <FormField label="Banka">
                    <input data-testid="cheque-form-bank" placeholder="Örn. Ziraat" value={form.bank} onChange={(e) => set("bank", e.target.value)} />
                  </FormField>
                  <FormField label="Çek no">
                    <input data-testid="cheque-form-no" value={form.cheque_no} onChange={(e) => set("cheque_no", e.target.value)} />
                  </FormField>
                </div>
              </>
            )}
            {editing && (
              <>
                <FormField label="Durum">
                  <select required data-testid="cheque-form-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                    <option value="pending">Bekliyor</option>
                    <option value="paid">Ödendi</option>
                    <option value="bounced">Karşılıksız</option>
                  </select>
                </FormField>
                {form.status === "paid" && (
                  <FormField label="Ödeme tarihi">
                    <input type="date" data-testid="cheque-form-paid-date" value={form.paid_date} onChange={(e) => set("paid_date", e.target.value)} />
                  </FormField>
                )}
              </>
            )}
            <FormField label="Not">
              <textarea data-testid="cheque-form-note" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="cheque-form-submit">{editing ? "Güncelle" : "Kaydet"}</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
